<template>
  <b-modal
    id="modal-participant-approval"
    ref="modalParticipantApproval"
    title="Approve Participants"
    size="xl"
    hide-footer
    @hidden="resetModal">
    <div v-if="selectedBatch">
      <!-- Batch Info Header -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-2">{{ selectedCourse?.title }} - {{ formatBatchLabel(selectedBatch) }}</h5>
            <div class="d-flex gap-2 align-items-center">
              <span class="badge" :class="getBatchStatusClass(selectedBatch.batch_status)">
                {{ selectedBatch.batch_status }}
              </span>
              <span class="text-muted">•</span>
              <small class="text-muted">
                Quota: {{ selectedBatch.quota }} | Enrolled: {{ selectedBatch.enrolled }}
              </small>
            </div>
          </div>
          <div>
            <span class="badge bg-primary">
              {{ selectedParticipants.length }} Selected
            </span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted mb-0">Loading participants...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="participants.length === 0" class="text-center py-5">
        <i class="fe fe-users" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No participants found</p>
      </div>

      <!-- Participants Table -->
      <div v-else class="table-responsive">
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th style="width: 40px;">
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="selectAll"
                    :checked="isAllSelected"
                    @change="toggleSelectAll">
                  <label class="custom-control-label" for="selectAll"></label>
                </div>
              </th>
              <th style="width: 40px;">No</th>
              <th>NIK</th>
              <th>Full Name</th>
              <th>Position</th>
              <th>Company</th>
              <th>Reason</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(participant, index) in participants" :key="participant.enrollment_id">
              <td>
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    :id="`participant-${participant.enrollment_id}`"
                    :value="participant.enrollment_id"
                    :checked="isParticipantSelected(participant.enrollment_id)"
                    @change="toggleParticipant(participant)"
                    :disabled="!isAwaitingApproval(participant)">
                  <label class="custom-control-label" :for="`participant-${participant.enrollment_id}`"></label>
                </div>
              </td>
              <td>{{ index + 1 }}</td>
              <td>
                <span class="badge badge-soft-primary">{{ participant.nik }}</span>
              </td>
              <td>{{ participant.fullname }}</td>
              <td>{{ participant.position_level }}</td>
              <td>
                <small class="text-muted">{{ participant.company }}</small>
              </td>
              <td>{{ participant.note }}</td>
              <td>
                <span
                  class="badge"
                  :class="getApprovalStatusClass(participant.status)">
                  {{ getApprovalStatusLabel(participant.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Action Buttons -->
      <div v-if="hasPendingParticipants" class="mt-4 d-flex gap-2 justify-content-end">
        <button 
          type="button" 
          class="btn btn-success"
          :disabled="isApproving || selectedParticipants.length === 0"
          @click="approveParticipants">
          <span v-if="isApproving">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Menyetujui...
          </span>
          <span v-else>
            <i class="fe fe-check me-2"></i>
            Approve
          </span>
        </button>
        <button 
          type="button" 
          class="btn btn-danger"
          :disabled="isRejecting || selectedParticipants.length === 0"
          @click="rejectParticipants">
          <span v-if="isRejecting">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Menolak...
          </span>
          <span v-else>
            <i class="fe fe-x me-2"></i>
            Reject
          </span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { formatBatchLabel } from '@/utils/batchIdentity';

const UNENROLLMENT_AWAIT_HSE = 'Waiting for HSE Training Unenrollment Approval';
const LEGACY_UNENROLLMENT_PENDING = 'Waiting for Cancellation Approval';

export default {
  name: 'ModalParticipantApproval',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedBatch: {
      type: Object,
      default: null
    },
    batchIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedParticipants: [],
      loading: false,
      isApproving: false,
      isRejecting: false
    };
  },
  watch: {
    selectedBatch: {
      immediate: true,
      handler(newBatch) {
        if (newBatch && newBatch.company_participants) {
          // Auto-select all pending participants with both enrollment_id and tna_id
          this.selectedParticipants = newBatch.company_participants
            .filter(p => this.isAwaitingApproval(p))
            .map(p => ({ enrollment_id: p.enrollment_id, tna_id: p.tna_id }));
        }
      }
    }
  },
  computed: {
    participants() {
      return this.selectedBatch?.company_participants || [];
    },
    
    hasPendingParticipants() {
      return this.participants.some(p => this.isAwaitingApproval(p));
    },
    
    isAllSelected() {
      const pendingParticipants = this.participants.filter(p => this.isAwaitingApproval(p));
      return pendingParticipants.length > 0 && 
             this.selectedParticipants.length === pendingParticipants.length;
    }
  },
  methods: {
    ...mapActions('mitraUnenrollmentReport', {
      approveParticipantsAction: 'approveParticipants',
      rejectParticipantsAction: 'rejectParticipants'
    }),
    formatBatchLabel,

    isAwaitingApproval(participant) {
      return [
        UNENROLLMENT_AWAIT_HSE,
        LEGACY_UNENROLLMENT_PENDING,
      ].includes(participant?.status);
    },

    getApprovalStatusLabel(status) {
      if (status === LEGACY_UNENROLLMENT_PENDING) return UNENROLLMENT_AWAIT_HSE;
      return status || 'Status unavailable';
    },

    getApprovalStatusClass(status) {
      if ([UNENROLLMENT_AWAIT_HSE, LEGACY_UNENROLLMENT_PENDING].includes(status)) return 'badge-soft-info';
      if (status === 'Cancelled') return 'badge-soft-success';
      if (status === 'Approved') return 'badge-soft-secondary';
      return 'badge-soft-secondary';
    },
    
    resetModal() {
      this.selectedParticipants = [];
    },
    
    closeModal() {
      this.$refs.modalParticipantApproval.hide();
    },
    
    isParticipantSelected(enrollmentId) {
      return this.selectedParticipants.some(p => p.enrollment_id === enrollmentId);
    },
    
    toggleParticipant(participant) {
      const index = this.selectedParticipants.findIndex(p => p.enrollment_id === participant.enrollment_id);
      if (index > -1) {
        // Remove from selection
        this.selectedParticipants.splice(index, 1);
      } else {
        // Add to selection with both enrollment_id and tna_id
        this.selectedParticipants.push({
          enrollment_id: participant.enrollment_id,
          tna_id: participant.tna_id
        });
      }
    },
    
    toggleSelectAll(event) {
      if (event.target.checked) {
        // Select all pending participants with both enrollment_id and tna_id
        this.selectedParticipants = this.participants
          .filter(p => this.isAwaitingApproval(p))
          .map(p => ({ enrollment_id: p.enrollment_id, tna_id: p.tna_id }));
      } else {
        // Deselect all
        this.selectedParticipants = [];
      }
    },
    
    async approveParticipants() {
      const result = await this.$swal({
        title: 'Setujui Peserta?',
        html: `You are about to approve <strong>${this.selectedParticipants.length}</strong> participant(s).<br/>This action cannot be undone.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, setujui',
        confirmButtonColor: '#00c9a7',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        this.isApproving = true;
        try {
          // Map to enrollment_id for approve request
          const enrollmentIds = this.selectedParticipants.map(p => p.enrollment_id);
          
          await this.approveParticipantsAction({
            batchId: this.selectedBatch.training_event_id,
            data: { approved: enrollmentIds }
          });

          this.$swal({
            title: 'Success',
            text: `${this.selectedParticipants.length} pengajuan unenrollment berhasil disetujui.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reset selections and close modal
          this.selectedParticipants = [];
          this.closeModal();
          
          // Refresh batch data and courses
          this.$emit('refresh-batch');
          this.$emit('refresh-courses');
          this.$emit('close-batch-modal');
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Peserta belum berhasil disetujui';
          
          this.$swal({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        } finally {
          this.isApproving = false;
        }
      }
    },
    
    async rejectParticipants() {
      const result = await this.$swal({
        title: 'Tolak Peserta?',
        html: `You are about to reject <strong>${this.selectedParticipants.length}</strong> participant(s).<br/>This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, tolak',
        confirmButtonColor: '#e63757',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        this.isRejecting = true;
        try {
          // Map to tna_id for reject request
          const tnaIds = this.selectedParticipants.map(p => p.tna_id);
          
          await this.rejectParticipantsAction({
            batchId: this.selectedBatch.training_event_id,
            data: { tna_ids: tnaIds }
          });

          this.$swal({
            title: 'Success',
            text: `${this.selectedParticipants.length} peserta berhasil ditolak.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reset selections and close modal
          this.selectedParticipants = [];
          this.closeModal();
          
          // Refresh batch data and courses
          this.$emit('refresh-batch');
          this.$emit('refresh-courses');
          this.$emit('close-batch-modal');
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Peserta belum berhasil ditolak';
          
          this.$swal({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        } finally {
          this.isRejecting = false;
        }
      }
    },
    
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.badge-soft-primary {
  color: #2c7be5;
  background-color: rgba(44, 123, 229, 0.1);
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.bg-primary {
  background-color: #2c7be5 !important;
  color: white;
}
</style>
